import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useDroppable } from '@dnd-kit/core';
import { FolderItem, FOLDER_COLORS } from 'mock/folder.data';

interface Props {
  folder: FolderItem;
  variant: 'grid' | 'list';
  onClick: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  onRename?: () => void;
  onShare?: () => void;
  isReadOnly?: boolean;
  isAdmin?: boolean;
}

const FolderSvg: React.FC<{ id: string; colorId: string; size?: number }> = ({ id, colorId, size = 44 }) => {
  const { startColor, endColor } = FOLDER_COLORS[colorId] ?? FOLDER_COLORS['3'];
  const height = Math.round(size * (90 / 122));
  const gBack = `fb-${id}`;
  const gFront = `ff-${id}`;
  const clip = `fc-${id}`;

  return (
    <svg className="folder-svg" xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 122 90" fill="none">
      <g clipPath={`url(#${clip})`}>
        <path d="M120.085 14.1861V83.5406C120.085 87.1075 117.184 90 113.607 90H8.55106C4.97387 90 2.073 87.1075 2.073 83.5406V6.45939C2.073 2.89252 4.97387 0 8.55106 0H31.6246C33.6237 0 35.5114 0.921109 36.7384 2.49462L40.8196 7.72672H113.607C117.184 7.72672 120.085 10.6192 120.085 14.1861Z" fill={`url(#${gBack})`} />
        <path d="M112.515 12.4408H9.64457C8.21348 12.4408 7.05334 13.5976 7.05334 15.0246V77.5476C7.05334 78.9746 8.21348 80.1314 9.64457 80.1314H112.515C113.946 80.1314 115.106 78.9746 115.106 77.5476V15.0246C115.106 13.5976 113.946 12.4408 112.515 12.4408Z" fill="white" />
        <g className="folder-svg__front">
          <path d="M4.66422 17.7207H117.494C118.924 17.7207 120.085 18.8782 120.085 20.3045V84.8325C120.085 87.685 117.763 90 114.903 90H7.25545C4.39473 90 2.073 87.685 2.073 84.8325V20.3045C2.073 18.8782 3.23387 17.7207 4.66422 17.7207Z" fill={`url(#${gFront})`} />
        </g>
      </g>
      <defs>
        <linearGradient id={gBack} x1="61.079" y1="0" x2="61.079" y2="90" gradientUnits="userSpaceOnUse">
          <stop stopColor={startColor} /><stop offset="0.27" stopColor={endColor} />
        </linearGradient>
        <linearGradient id={gFront} x1="61.079" y1="17.72" x2="61.079" y2="90" gradientUnits="userSpaceOnUse">
          <stop stopColor={startColor} /><stop offset="1" stopColor={endColor} />
        </linearGradient>
        <clipPath id={clip}><rect width="122" height="90" fill="white" /></clipPath>
      </defs>
    </svg>
  );
};

const DROPDOWN_MENU_STYLE: React.CSSProperties = { minWidth: 200 };

const buildMenu = (
  folder: FolderItem, 
  onMove?: () => void, 
  onDelete?: () => void, 
  onRename?: () => void, 
  onShare?: () => void, 
  isAdmin?: boolean
): MenuProps['items'] => {
  const items: MenuProps['items'] = [];

  if (onShare) {
    items.push({ 
      key: 'share', 
      label: 'Chia sẻ', 
      onClick: (info) => { 
        info.domEvent.stopPropagation(); 
        onShare(); 
      } 
    });
  }

  if (!folder.isAdminUpload || isAdmin) {
    if (onRename) {
      items.push({ key: 'rename', label: 'Đổi tên', onClick: (info) => { info.domEvent.stopPropagation(); onRename(); } });
    }
    if (onMove) {
      items.push({ key: 'move', label: 'Di chuyển', onClick: (info) => { info.domEvent.stopPropagation(); onMove(); } });
    }
    if (onDelete) {
      items.push({ type: 'divider' });
      items.push({ key: 'delete', label: 'Xóa', danger: true, onClick: (info) => { info.domEvent.stopPropagation(); onDelete(); } });
    }
  }

  return items;
};

const MoreBtn = React.forwardRef<HTMLButtonElement, { onClick: (e: React.MouseEvent) => void }>(
  ({ onClick }, ref) => (
    <button ref={ref} className="lib-more-btn" onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
      </svg>
    </button>
  )
);

const FolderCard: React.FC<Props> = ({ 
  folder, 
  variant, 
  onClick, 
  onMove, 
  onDelete, 
  onRename, 
  onShare, 
  isReadOnly, 
  isAdmin 
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: folder.id });

  const currentUserId = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        return u.id ? Number(u.id) : null;
      }
    } catch (e) {
      return null;
    }
    return null;
  })();

  const isOwner = folder.createdBy !== undefined && currentUserId !== null && Number(folder.createdBy) === Number(currentUserId);
  
  const menuItems = buildMenu(folder, onMove, onDelete, onRename, onShare, isAdmin);
  const menuProps: MenuProps = { 
    items: menuItems, 
    style: DROPDOWN_MENU_STYLE 
  };

  const showMenu = (isAdmin || isOwner) && (menuItems?.length ?? 0) > 0;

  if (variant === 'list') {
    return (
      <div
        ref={setNodeRef}
        className={`lib-list-row${isOver ? ' lib-list-row--drop-over' : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        <div className="lib-list-row__icon">
          <FolderSvg id={`l-${folder.id}`} colorId={folder.colorId} size={28} />
        </div>
        <span className="lib-list-row__name">{folder.name}</span>
        <span className="lib-list-row__cell">{folder.owner}</span>
        <span className="lib-list-row__cell">—</span>
        <span className="lib-list-row__cell">{folder.updatedAt.slice(0, 10)}</span>
        {showMenu && (
          <Dropdown menu={menuProps} trigger={['click']} placement="bottomRight">
            <MoreBtn onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        )}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`lib-folder-card${isOver ? ' lib-folder-card--drop-over' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="lib-folder-card__icon">
        <FolderSvg id={folder.id} colorId={folder.colorId} size={44} />
      </div>
      <span className="lib-folder-card__name" title={folder.name}>{folder.name}</span>
      {showMenu && (
        <Dropdown menu={menuProps} trigger={['click']} placement="bottomRight">
          <MoreBtn onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      )}
    </div>
  );
};

export default FolderCard;